import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { RootState } from '../store';
import { setLanguage } from '@digitaltattoo/redux-slices';

import { AuthOptionsView } from './AuthOptionsView';
import { GuestBookingForm } from './GuestBookingForm';
import { AccountCreationForm } from './AccountCreationForm';
import { PrivacyNotice } from './PrivacyNotice';

import { LoginPage } from '../pages/LoginPage';

import { ArrowLeft, User, LogOut, AlertTriangle, Shield, Mail, Phone } from 'lucide-react';

import { logout, updateUser } from '../store/slices/authSlice';

import { isMinor as computeIsMinor } from '@digitaltattoo/shared-utils';

import { clientAuthApi } from '../services/clientAuthApi';
import { useFrontoffWorkingMode } from '../hooks/useSystemStatus';

interface LegalGuardianData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;

  relationshipToMinor: 'parent' | 'legal_guardian' | 'other';

  relationshipDetails?: string;
}

export interface ClientData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  isMinor?: boolean | null;

  preferredLanguage: 'fr' | 'en';

  accountCreated?: boolean;

  /** True when register was followed by an immediate successful login. */
  autoLoggedIn?: boolean;

  isAnonymous?: boolean;
  anonymousCode?: string;

  legalGuardian?: LegalGuardianData;

  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
}

type AuthOption = 'login' | 'create' | 'guest' | null;

interface CustomerDetailsStepProps {
  onComplete: (clientId: string, clientData: ClientData) => void;

  onBack?: () => void;

  /** Called when user logs out from the "book for someone else" flow (piercing only) */
  onLogout?: () => void;

  initialData?: Partial<ClientData>;

  contactInfo?: {
    email?: string;
    phone?: string;
  };

  lookupStatus?: 'unknown' | 'not_found' | 'found' | 'create_new';

  bookingType: 'tattoo' | 'piercing';

  requireLegalGuardian?: boolean;

  preferredLanguage?: 'fr' | 'en';

  /** When true, parent provides navigation (e.g. wizard) - hide internal Prev/Next buttons */
  hideNavigation?: boolean;

  initialAuthOption?: AuthOption;

  lockToAnonymous?: boolean;

  /** Controls whether the public piercing journey offers anonymous booking. */
  allowAnonymousBooking?: boolean;
}

export function CustomerDetailsStep({
  onComplete,
  onBack,
  onLogout,
  initialData,
  contactInfo,
  lookupStatus = 'unknown',
  bookingType,
  requireLegalGuardian = false,
  preferredLanguage: propPreferredLanguage,
  hideNavigation = false,
  initialAuthOption = null,
  lockToAnonymous = false,
  allowAnonymousBooking = false,
}: CustomerDetailsStepProps) {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { isLimited, loading: workingModeLoading } = useFrontoffWorkingMode();
  const [authOption, setAuthOption] = useState<AuthOption>(
    lockToAnonymous && allowAnonymousBooking ? 'guest' : initialAuthOption
  );
  useEffect(() => {
    const guestDisabled =
      (isLimited && bookingType !== 'piercing') ||
      (bookingType === 'piercing' && !allowAnonymousBooking);
    if (guestDisabled && authOption === 'guest') {
      setAuthOption(null);
    }
  }, [isLimited, authOption, bookingType, allowAnonymousBooking]);
  const [clientId, setClientId] = useState<string | null>(null);

  const [clientData, setClientData] = useState<ClientData | null>(null);

  const [email, setEmail] = useState('');

  const [phone, setPhone] = useState('');

  const [emailExists, setEmailExists] = useState(false);

  const [phoneExists, setPhoneExists] = useState(false);

  const [loading, setLoading] = useState(false);

  const hasCompletedRef = useRef<string | null>(null);

  const [guardianForm, setGuardianForm] = useState<LegalGuardianData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationshipToMinor: 'parent',
  });

  const [guardianErrors, setGuardianErrors] = useState<Record<string, string>>({});

  const [savingGuardian, setSavingGuardian] = useState(false);

  const piercingMinorWarningRef = useRef<HTMLDivElement>(null);

  const didScrollMinorWarning = useRef(false);

  /*
   * Determine preferred language:
   *
   * prop > user > i18n > fr
   */
  const preferredLanguage =
    propPreferredLanguage ||
    (user as any)?.preferredLanguage ||
    (i18n.language.startsWith('en') ? 'en' : 'fr') ||
    'fr';

  /*
   * =========================================================
   * LOAD CLIENT
   * =========================================================
   */

  const loadClientData = useCallback(async () => {
    if (!user) {
      setLoading(false);

      return;
    }

    setLoading(true);

    try {
      const userId = user.id;

      if (userId) {
        const clientDataValue: ClientData = {
          id: userId,

          firstName: user.firstName || '',

          lastName: user.lastName || '',

          email: user.email || '',

          phone: user.phone || '',

          dateOfBirth: user.dateOfBirth || '',
          isMinor: user.isMinor,

          preferredLanguage: (user as any).preferredLanguage || preferredLanguage,
        };

        setClientId(userId);

        setClientData(clientDataValue);

        const userLang = (user as any).preferredLanguage || preferredLanguage;

        if (userLang && userLang !== i18n.language) {
          i18n.changeLanguage(userLang);

          dispatch(setLanguage(userLang as 'fr' | 'en'));
        }
      } else {
        console.warn('User object missing id field');

        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading client data:', error);

      setLoading(false);
    }
  }, [user, preferredLanguage, i18n, dispatch]);

  /*
   * If authenticated,
   * try to load client data.
   */
  useEffect(() => {
    if (isAuthenticated && user && !clientId) {
      loadClientData();
    }
  }, [isAuthenticated, user, clientId, loadClientData]);

  /*
   * Initial data
   */
  useEffect(() => {
    if (initialData?.id && !clientId) {
      setClientId(initialData.id);

      setClientData({
        id: initialData.id,

        firstName: initialData.firstName || '',

        lastName: initialData.lastName || '',

        email: initialData.email || '',

        phone: initialData.phone || '',

        dateOfBirth: initialData.dateOfBirth || '',
        isMinor: initialData.isMinor,

        preferredLanguage: initialData.preferredLanguage || preferredLanguage,

        legalGuardian: initialData.legalGuardian,
      });
    }
  }, [initialData, clientId, preferredLanguage]);

  /*
   * Scroll once to the piercing
   * minor warning.
   */
  useEffect(() => {
    const userIsMinor = user?.dateOfBirth ? computeIsMinor(user.dateOfBirth) : !!user?.isMinor;

    const shouldWarn =
      bookingType === 'piercing' && isAuthenticated && !!clientId && !!clientData && userIsMinor;

    if (shouldWarn && !didScrollMinorWarning.current) {
      didScrollMinorWarning.current = true;

      requestAnimationFrame(() => {
        piercingMinorWarningRef.current?.scrollIntoView({
          behavior: 'smooth',

          block: 'nearest',
        });
      });
    }

    if (!shouldWarn) {
      didScrollMinorWarning.current = false;
    }
  }, [bookingType, isAuthenticated, clientId, clientData, user]);

  /*
   * Keep provided contact info.
   */
  useEffect(() => {
    if (contactInfo?.email) {
      setEmail(contactInfo.email);
    } else if (initialData?.email) {
      setEmail(initialData.email);
    }

    if (contactInfo?.phone) {
      setPhone(contactInfo.phone);
    } else if (initialData?.phone) {
      setPhone(initialData.phone);
    }
  }, [contactInfo, initialData]);

  /*
   * Auto-complete only for booking
   * types other than piercing/tattoo.
   *
   * Piercing/tattoo require explicit
   * confirmation.
   */
  useEffect(() => {
    if (
      isAuthenticated &&
      user &&
      clientId &&
      clientData &&
      !authOption &&
      bookingType !== 'piercing' &&
      bookingType !== 'tattoo'
    ) {
      if (hasCompletedRef.current !== clientId) {
        hasCompletedRef.current = clientId;

        onComplete(clientId, clientData);
      }
    }
  }, [isAuthenticated, user, clientId, clientData, authOption, bookingType, onComplete]);

  /*
   * =========================================================
   * LOGIN
   * =========================================================
   */

  const handleLogin = () => {
    /*
     * In WordPress context,
     * use inline login.
     */
    const wpConfig = (window as any).wpReactConfig;

    if (!wpConfig) {
      const base = window.location.pathname;

      const stepParam = 'step=customer';

      const returnUrl = window.location.search
        ? `${base}?${stepParam}&${window.location.search.slice(1)}`
        : `${base}?${stepParam}`;

      navigate('/login', {
        state: {
          from: {
            pathname: returnUrl,
          },
        },
      });
    }
  };

  /*
   * =========================================================
   * GUEST
   * =========================================================
   */

  const handleGuestComplete = (guestClientData: ClientData) => {
    setClientId(guestClientData.id);

    setClientData({
      ...guestClientData,
      accountCreated: false,
    });

    hasCompletedRef.current = guestClientData.id;

    onComplete(guestClientData.id, {
      ...guestClientData,
      accountCreated: false,
    });
  };

  const handleBackToOptions = () => {
    setAuthOption(null);

    setClientId(null);

    setClientData(null);
  };

  /*
   * =========================================================
   * CLIENT DATA AVAILABLE
   * =========================================================
   */

  if (clientId && clientData) {
    /*
     * Piercing + tattoo :
     * explicit confirmation.
     */
    if ((bookingType === 'piercing' || bookingType === 'tattoo') && isAuthenticated) {
      const displayName =
        [clientData.firstName, clientData.lastName].filter(Boolean).join(' ') || clientData.email;

      const userIsMinor = user?.dateOfBirth ? computeIsMinor(user.dateOfBirth) : !!user?.isMinor;

      const tattooBlockedForMinor = bookingType === 'tattoo' && userIsMinor;

      const piercingMinorWarning = bookingType === 'piercing' && userIsMinor;

      const hasGuardianData = !!(user as any)?.legalGuardian?.firstName;

      const needsGuardianInput = piercingMinorWarning && !hasGuardianData;

      /*
       * =====================================================
       * GUARDIAN VALIDATION
       * =====================================================
       */

      const validateGuardian = (): boolean => {
        const errs: Record<string, string> = {};

        if (!guardianForm.firstName.trim()) {
          errs['guardian.firstName'] = t('customerDetails.guest.errors.guardianFirstNameRequired');
        }

        if (!guardianForm.lastName.trim()) {
          errs['guardian.lastName'] = t('customerDetails.guest.errors.guardianLastNameRequired');
        }

        if (!guardianForm.email.trim()) {
          errs['guardian.email'] = t('customerDetails.guest.errors.guardianEmailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guardianForm.email)) {
          errs['guardian.email'] = t('customerDetails.guest.errors.guardianEmailInvalid');
        }

        if (!guardianForm.phone.trim()) {
          errs['guardian.phone'] = t('customerDetails.guest.errors.guardianPhoneRequired');
        }

        setGuardianErrors(errs);

        return Object.keys(errs).length === 0;
      };

      /*
       * =====================================================
       * SAVE GUARDIAN
       * =====================================================
       */

      const handleSaveGuardianAndContinue = async () => {
        if (!validateGuardian()) {
          return;
        }

        setSavingGuardian(true);

        try {
          const token = localStorage.getItem('client_token');

          if (!token || !user) {
            return;
          }

          const result = await clientAuthApi.updateProfile(token, {
            firstName: user.firstName,

            lastName: user.lastName,

            phone: user.phone,

            dateOfBirth: user.dateOfBirth || '',

            legalGuardian: {
              firstName: guardianForm.firstName.trim(),

              lastName: guardianForm.lastName.trim(),

              email: guardianForm.email.trim(),

              phone: guardianForm.phone.trim(),

              relationshipToMinor: guardianForm.relationshipToMinor,

              relationshipDetails: guardianForm.relationshipDetails,
            },
          });

          dispatch(updateUser(result.user));

          const updatedClientData: ClientData = {
            ...clientData,

            legalGuardian: {
              firstName: guardianForm.firstName.trim(),

              lastName: guardianForm.lastName.trim(),

              email: guardianForm.email.trim(),

              phone: guardianForm.phone.trim(),

              relationshipToMinor: guardianForm.relationshipToMinor,

              relationshipDetails: guardianForm.relationshipDetails,
            },
          };

          onComplete(clientId, updatedClientData);
        } catch (err) {
          console.error('Failed to save guardian:', err);

          setGuardianErrors({
            general: t('auth.account.profile.errors.updateFailed', 'Failed to save guardian data'),
          });
        } finally {
          setSavingGuardian(false);
        }
      };

      /*
       * =====================================================
       * LOGOUT / BOOK FOR SOMEONE ELSE
       * =====================================================
       */

      const handleLogoutForSomeoneElse = () => {
        setClientId(null);

        setClientData(null);

        hasCompletedRef.current = null;

        dispatch(logout());

        onLogout?.();
      };

      /*
       * =====================================================
       * AUTHENTICATED CONFIRMATION VIEW
       * =====================================================
       */

      return (
        <div className="space-y-6">
          {/* Tattoo minor blocked */}
          {tattooBlockedForMinor && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-red-600" />
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-1">
                    {t('customerDetails.tattooMinorBlocked.title', 'Tattoo not permitted')}
                  </h3>

                  <p className="text-sm text-red-700">
                    {t(
                      'customerDetails.tattooMinorBlocked.description',
                      'Under French law, tattooing is not allowed for minors under 18, even with parental consent. You cannot proceed with a tattoo booking.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Piercing minor warning */}
          {piercingMinorWarning && (
            <div
              ref={piercingMinorWarningRef}
              className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6 shadow-md"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-amber-700" />
                </div>

                <h3 className="text-lg font-bold text-amber-900">
                  {t(
                    'customerDetails.piercingMinorWarning.title',
                    'Minor — legal guardian required'
                  )}
                </h3>
              </div>

              <p className="text-base text-amber-800 leading-relaxed">
                {t(
                  'customerDetails.piercingMinorWarning.description',
                  'As you are under 18, your legal guardian must give their written consent and be physically present during the piercing appointment.'
                )}
              </p>

              <p className="mt-3 text-sm font-semibold text-amber-900">
                {t(
                  'customerDetails.piercingMinorWarning.reminder',
                  'Please make sure your legal guardian accompanies you on the day of the appointment.'
                )}
              </p>
            </div>
          )}

          {/* Guardian already on file */}
          {piercingMinorWarning && hasGuardianData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />

                <div className="flex-1">
                  <h3 className="font-semibold text-green-800 mb-1">
                    {t('customerDetails.guest.legalGuardian', 'Legal Guardian')}
                  </h3>

                  <p className="text-sm text-green-700">
                    {(user as any).legalGuardian.firstName} {(user as any).legalGuardian.lastName} —{' '}
                    {(user as any).legalGuardian.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Guardian form */}
          {needsGuardianInput && (
            <div className="bg-white border-2 border-amber-300 rounded-xl p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-amber-600" />

                <h3 className="text-lg font-semibold text-textPrimary">
                  {t('customerDetails.guest.legalGuardian', 'Legal Guardian Information')}
                </h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                {t(
                  'customerDetails.piercingMinorGuardianPrompt',
                  "Please provide your legal guardian's details to continue with the booking."
                )}
              </p>

              {guardianErrors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{guardianErrors.general}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('customerDetails.guest.guardianFirstName', 'First Name')}
                  </label>

                  <input
                    type="text"
                    value={guardianForm.firstName}
                    onChange={(e) =>
                      setGuardianForm((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      guardianErrors['guardian.firstName'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />

                  {guardianErrors['guardian.firstName'] && (
                    <p className="text-sm text-red-600 mt-1">
                      {guardianErrors['guardian.firstName']}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('customerDetails.guest.guardianLastName', 'Last Name')}
                  </label>

                  <input
                    type="text"
                    value={guardianForm.lastName}
                    onChange={(e) =>
                      setGuardianForm((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }))
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      guardianErrors['guardian.lastName'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />

                  {guardianErrors['guardian.lastName'] && (
                    <p className="text-sm text-red-600 mt-1">
                      {guardianErrors['guardian.lastName']}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('customerDetails.guest.guardianEmail', 'Email')}
                  </label>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-gray-400" />
                    </div>

                    <input
                      type="email"
                      value={guardianForm.email}
                      onChange={(e) =>
                        setGuardianForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        guardianErrors['guardian.email'] ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {guardianErrors['guardian.email'] && (
                    <p className="text-sm text-red-600 mt-1">{guardianErrors['guardian.email']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('customerDetails.guest.guardianPhone', 'Phone')}
                  </label>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-4 w-4 text-gray-400" />
                    </div>

                    <input
                      type="tel"
                      value={guardianForm.phone}
                      onChange={(e) =>
                        setGuardianForm((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        guardianErrors['guardian.phone'] ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {guardianErrors['guardian.phone'] && (
                    <p className="text-sm text-red-600 mt-1">{guardianErrors['guardian.phone']}</p>
                  )}
                </div>
              </div>

              <div className="max-w-xs">
                <label className="block text-sm font-medium mb-1">
                  {t('customerDetails.guest.relationship', 'Relationship')}
                </label>

                <select
                  value={guardianForm.relationshipToMinor}
                  onChange={(e) =>
                    setGuardianForm((prev) => ({
                      ...prev,

                      relationshipToMinor: e.target.value as any,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="parent">
                    {t('customerDetails.guest.relationshipOptions.parent', 'Parent')}
                  </option>

                  <option value="legal_guardian">
                    {t('customerDetails.guest.relationshipOptions.legalGuardian', 'Legal Guardian')}
                  </option>

                  <option value="other">
                    {t('customerDetails.guest.relationshipOptions.other', 'Other')}
                  </option>
                </select>
              </div>
            </div>
          )}

          {/*
           * ===================================================
           * CONFIRMATION EN 2 COLONNES
           * ===================================================
           */}
          {!tattooBlockedForMinor && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
              {/*
               * =================================================
               * COLONNE GAUCHE
               * =================================================
               */}
              <div className="flex flex-col rounded-xl border border-blue-200 bg-blue-50 p-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-11 h-11 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      Confirmer la réservation
                    </h3>

                    <p className="text-sm text-blue-700">
                      {t('customerDetails.authenticated.confirmMessage', {
                        name: displayName,
                      })}
                    </p>

                    <div className="mt-4 rounded-lg border border-blue-200 bg-white/20 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wide mb-1">
                        Compte utilisé
                      </p>

                      <p className="font-semibold text-blue-900 break-words">{displayName}</p>

                      <p className="mt-1 text-sm text-blue-700 break-words">{clientData.email}</p>
                    </div>
                  </div>
                </div>

                {!hideNavigation && (
                  <div className="mt-auto pt-6">
                    {needsGuardianInput ? (
                      <button
                        type="button"
                        onClick={handleSaveGuardianAndContinue}
                        disabled={savingGuardian}
                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {savingGuardian ? 'Enregistrement...' : 'Oui, continuer avec mon compte'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onComplete(clientId, clientData)}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                      >
                        Oui, continuer avec mon compte
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/*
               * =================================================
               * COLONNE DROITE
               * =================================================
               */}
              <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-11 h-11 bg-gray-100 rounded-full flex items-center justify-center">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Réserver pour une autre personne
                    </h3>

                    <p className="text-sm leading-relaxed text-gray-600">
                      Si vous souhaitez réserver pour une autre personne, déconnectez-vous puis
                      recommencez le processus.
                    </p>
                  </div>
                </div>

                {!hideNavigation && (
                  <div className="mt-auto pt-6">
                    <button
                      type="button"
                      onClick={handleLogoutForSomeoneElse}
                      className="btn-secondary w-full flex items-center justify-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />

                      <span>Me déconnecter et recommencer</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    /*
     * Tattoo or non-authenticated:
     * standard summary with Next.
     */
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
            </div>

            <div className="flex-1">
              <h3 className="font-semibold text-green-800 mb-1">
                {t('customerDetails.authenticated.title')}
              </h3>

              <p className="text-sm text-green-700 mb-2">
                {t('customerDetails.authenticated.bookingFor')}
              </p>

              <p className="text-sm font-medium text-green-800">{clientData.email}</p>
            </div>
          </div>
        </div>

        <p className="text-sm text-green-700">{t('customerDetails.authenticated.usingAccount')}</p>

        {!hideNavigation && (
          <div className="flex items-center justify-between">
            {onBack ? (
              <button onClick={onBack} className="btn-secondary flex items-center space-x-2">
                <ArrowLeft className="w-4 h-4" />

                <span>{t('common.previous')}</span>
              </button>
            ) : (
              <div />
            )}

            <button
              onClick={() => onComplete(clientId, clientData)}
              className="btn-primary flex items-center space-x-2"
            >
              <span>{t('common.next')}</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  /*
   * =========================================================
   * AUTHENTICATED — LOADING
   * =========================================================
   */

  if (isAuthenticated && loading) {
    return (
      <div className="text-center py-8">
        <p>{t('customerDetails.loading')}</p>
      </div>
    );
  }

  /*
   * =========================================================
   * AUTHENTICATED — NO CLIENT YET
   * =========================================================
   */

  if (isAuthenticated && user && !authOption && !clientId) {
    if (loading) {
      return (
        <div className="text-center py-8">
          <p>{t('customerDetails.loading')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
            </div>

            <div className="flex-1">
              <h3 className="font-semibold text-green-800 mb-1">
                {t('customerDetails.authenticated.title')}
              </h3>

              <p className="text-sm text-green-700 mb-2">
                {t('customerDetails.authenticated.bookingFor')}
              </p>

              <p className="text-sm font-medium text-green-800">{user.email}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /*
   * =========================================================
   * AUTH OPTIONS
   * =========================================================
   */

  if (!isAuthenticated && !authOption) {
    return (
      <div className="space-y-6">
        <PrivacyNotice variant="booking" />

        <AuthOptionsView
          onSelectOption={(option) => {
            setAuthOption(option);

            if (option === 'login') {
              handleLogin();
            }
          }}
          emailExists={emailExists}
          phoneExists={phoneExists}
          contactInfo={{
            email,
            phone,
          }}
          lookupStatus={lookupStatus}
          hideGuestOption={
            bookingType === 'tattoo' ||
            (bookingType === 'piercing' && !allowAnonymousBooking)
          }
          anonymousGuest={bookingType === 'piercing'}
        />
      </div>
    );
  }

  /*
   * =========================================================
   * INLINE LOGIN
   * =========================================================
   */

  if (authOption === 'login' && !isAuthenticated) {
    return (
      <div className="space-y-6">
        <LoginPage
          inline
          onSuccess={() => {
            /*
             * Redux auth state
             * will trigger the useEffect.
             */
          }}
          onCancel={handleBackToOptions}
        />
      </div>
    );
  }

  /*
   * =========================================================
   * CREATE ACCOUNT
   * =========================================================
   */

  if (authOption === 'create') {
    const creationInitialValues = {
      firstName: initialData?.firstName || '',

      lastName: initialData?.lastName || '',

      email: contactInfo?.email || initialData?.email || '',

      phone: contactInfo?.phone || initialData?.phone || '',

      dateOfBirth: initialData?.dateOfBirth || '',
    };

    return (
      <div className="space-y-6">
        <PrivacyNotice variant="booking" />

        <AccountCreationForm
          onSuccess={(clientData) => {
            setClientId(clientData.id);

            setClientData(clientData);

            hasCompletedRef.current = clientData.id;

            onComplete(clientData.id, clientData);
          }}
          onCancel={handleBackToOptions}
          preferredLanguage={preferredLanguage}
          initialValues={creationInitialValues}
          bookingType={bookingType}
        />
      </div>
    );
  }

if (
  authOption === 'guest' &&
  ((bookingType === 'piercing' && allowAnonymousBooking) ||
    (bookingType !== 'piercing' && !isLimited))
) {
  return (
    <div className="space-y-6">
      <PrivacyNotice variant="booking" />

      <GuestBookingForm
        anonymous={bookingType === 'piercing'}
        requireLegalGuardian={
          requireLegalGuardian
        }
        preferredLanguage={
          preferredLanguage
        }
        initialValues={
          initialData
        }
        onComplete={
          handleGuestComplete
        }
        onBack={
          lockToAnonymous ? onBack : handleBackToOptions
        }
      />
    </div>
  );
}

  if (isAuthenticated) {
    return (
      <div className="text-center py-8">
        <p>{t('customerDetails.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PrivacyNotice variant="booking" />

      <AuthOptionsView
        onSelectOption={(option) => {
          setAuthOption(option);

          if (option === 'login') {
            handleLogin();
          }
        }}
        emailExists={emailExists}
        phoneExists={phoneExists}
        contactInfo={{
          email,
          phone,
        }}
        lookupStatus={lookupStatus}
        hideGuestOption={
          bookingType === 'tattoo' ||
          (bookingType === 'piercing' && !allowAnonymousBooking)
        }
        anonymousGuest={bookingType === 'piercing'}
      />
    </div>
  );
}
